/**
 * sourcePackageYMap.js
 *
 * CRUD API for source package data stored in Y.Map("sourcePackage").
 * Fully client-owned — no network, no UI, no Zustand dependencies.
 *
 * Structure inside the Y.Doc:
 *
 *   Y.Map("sourcePackage")
 *     ├── folders: Y.Map<folderId, Y.Map>   ← flat map of folder entries
 *     └── items:   Y.Map<itemId, Y.Map>     ← flat map of item entries
 *
 * Each folder/item Y.Map contains only primitive values (strings, numbers, null).
 * Nested objects (start, end) are stored as JSON strings.
 *
 * Every write is wrapped in ydoc.transact() for atomicity — single Y.js update
 * message per logical operation.
 * Origin is always `null` so writes are tracked by Y.UndoManager (undoable).
 */

import * as Y from 'yjs';
import {generateUUID} from '../helpers/utils';

// ---------------------------------------------------------------------------
// Internal key constants
// ---------------------------------------------------------------------------

const FOLDERS_KEY = 'folders';
const ITEMS_KEY = 'items';
const STEP = 10; // default gap between positional values

const isYMap = (value) => value instanceof Y.Map;

function toYMapEntry(value) {
    if (isYMap(value)) return value;

    const entryMap = new Y.Map();
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.entries(value).forEach(([key, entryValue]) => {
            entryMap.set(key, entryValue);
        });
    }

    return entryMap;
}

function normalizeSubMapInTransaction(ySourcePackage, key) {
    const current = ySourcePackage.get(key);
    if (isYMap(current)) return current;

    const normalized = new Y.Map();
    if (current && typeof current === 'object' && !Array.isArray(current)) {
        Object.entries(current).forEach(([entryId, entryValue]) => {
            normalized.set(entryId, toYMapEntry(entryValue));
        });
    }

    ySourcePackage.set(key, normalized);
    return normalized;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Get or create a sub-map under ySourcePackage by key.
 * Must be called inside a ydoc.transact() block when creating.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string} key  'folders' or 'items'
 * @returns {import('yjs').Map}
 */
function getOrCreateSubMap(ySourcePackage, key) {
    if (!ySourcePackage.has(key)) {
        const map = new Y.Map();
        ySourcePackage.set(key, map);
        return map;
    }
    return normalizeSubMapInTransaction(ySourcePackage, key);
}

/**
 * Get the folders sub-map, or null if ySourcePackage is falsy.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @returns {import('yjs').Map|null}
 */
function getFoldersMap(ySourcePackage) {
    if (!ySourcePackage) return null;
    const folders = ySourcePackage.get(FOLDERS_KEY);
    return isYMap(folders) ? folders : null;
}

/**
 * Get the items sub-map, or null if ySourcePackage is falsy.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @returns {import('yjs').Map|null}
 */
function getItemsMap(ySourcePackage) {
    if (!ySourcePackage) return null;
    const items = ySourcePackage.get(ITEMS_KEY);
    return isYMap(items) ? items : null;
}

/**
 * Deserialise a folder or item Y.Map into a plain JS object.
 * Parses JSON-encoded `start` and `end` fields.
 *
 * @param {string} id
 * @param {import('yjs').Map} yMap
 * @returns {Object}
 */
function deserialise(id, yMap) {
    const obj = {id};
    if (isYMap(yMap)) {
        yMap.forEach((value, key) => {
            obj[key] = value;
        });
    } else if (yMap && typeof yMap === 'object' && !Array.isArray(yMap)) {
        Object.entries(yMap).forEach(([key, value]) => {
            obj[key] = value;
        });
    }

    // Parse JSON-encoded nested objects
    if (typeof obj.start === 'string') {
        try {
            obj.start = JSON.parse(obj.start);
        } catch {
            obj.start = null;
        }
    }
    if (typeof obj.end === 'string') {
        try {
            obj.end = JSON.parse(obj.end);
        } catch {
            obj.end = null;
        }
    }

    return obj;
}

/**
 * Repair legacy/non-Y.Map sourcePackage subtrees in place.
 * This is safe to call repeatedly; it is a no-op when schema is already valid.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} ySourcePackage
 */
export function ensureSourcePackageSchema(ydoc, ySourcePackage) {
    if (!ydoc || !ySourcePackage) return;

    ydoc.transact(() => {
        normalizeSubMapInTransaction(ySourcePackage, FOLDERS_KEY);
        normalizeSubMapInTransaction(ySourcePackage, ITEMS_KEY);
    }, null);
}

// ---------------------------------------------------------------------------
// Exported utility
// ---------------------------------------------------------------------------

/**
 * Derive a stable localId from item data.
 * Format: `docId:startSegIdx:startSegOff:endSegIdx:endSegOff`
 *
 * Mirrors the logic in `referencesYMap.js`.
 *
 * @param {Object} data
 * @returns {string|null}
 */
export function generateLocalId(data) {
    if (!data) return null;
    if (data.localId) return data.localId;

    const s = data.start || {};
    const e = data.end || {};
    const sIdx = s.segmentIndex != null ? s.segmentIndex : data.index;
    const sOff = s.segmentOffset != null ? s.segmentOffset : (data.offset ?? 0);
    const eIdx = e.segmentIndex != null ? e.segmentIndex : sIdx;
    const eOff = e.segmentOffset != null ? e.segmentOffset : sOff;

    if (data.docId != null && sIdx != null && sOff != null && eIdx != null && eOff != null) {
        return [data.docId, sIdx, sOff, eIdx, eOff].join(':');
    }
    return null;
}

/**
 * Compute a fractional midpoint position for inserting at `destinationIndex`
 * among `siblings` (sorted by position ascending).
 *
 * Mirrors the algorithm from `sourcePackageSlice.js`.
 *
 * @param {Array<{position: number}>} siblings  Siblings sorted by position asc
 * @param {number} destinationIndex
 * @returns {number}
 */
export function computePosition(siblings, destinationIndex) {
    if (siblings.length === 0) return STEP;
    if (destinationIndex <= 0) {
        const next = siblings[0]?.position ?? 0;
        return next - STEP;
    }
    if (destinationIndex >= siblings.length) {
        const prev = siblings[siblings.length - 1]?.position ?? 0;
        return prev + STEP;
    }
    const prev = siblings[destinationIndex - 1]?.position ?? 0;
    const next = siblings[destinationIndex]?.position ?? prev + STEP;
    return (prev + next) / 2;
}

/**
 * Compute the append position for a new item/folder at the end of its sibling group.
 * Equivalent to `computePosition(siblings, siblings.length)`.
 *
 * @param {Array<{position: number}>} siblings  Siblings sorted by position asc
 * @returns {number}
 */
export function computeAppendPosition(siblings) {
    if (siblings.length === 0) return STEP;
    const last = siblings[siblings.length - 1]?.position ?? 0;
    return last + STEP;
}

// ---------------------------------------------------------------------------
// Public write API — Folders
// ---------------------------------------------------------------------------

/**
 * Create a new folder in the source package.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage  Root Y.Map("sourcePackage")
 * @param {{
 *   name:           string,
 *   parentFolderId: string|null,
 *   position:       number,
 *   userId:         string,
 * }} opts
 * @returns {string}  The generated folderId
 */
export function addFolder(ydoc, ySourcePackage, {name, parentFolderId = null, position, userId}) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!name) throw new Error('[sourcePackageYMap] name is required');

    const folderId = generateUUID();

    ydoc.transact(() => {
        const foldersMap = getOrCreateSubMap(ySourcePackage, FOLDERS_KEY);

        const folderMap = new Y.Map();
        folderMap.set('name', name);
        folderMap.set('parentFolderId', parentFolderId);
        folderMap.set('position', position ?? STEP);
        folderMap.set('createdAt', Date.now());
        folderMap.set('createdBy', userId ?? null);

        foldersMap.set(folderId, folderMap);
    }, null);

    return folderId;
}

/**
 * Rename a folder.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             folderId
 * @param {string}             name
 */
export function renameFolder(ydoc, ySourcePackage, folderId, name) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!folderId) throw new Error('[sourcePackageYMap] folderId is required');
    if (!name) throw new Error('[sourcePackageYMap] name is required');

    ydoc.transact(() => {
        const foldersMap = getFoldersMap(ySourcePackage);
        if (!foldersMap) return;
        const folderMap = foldersMap.get(folderId);
        if (!folderMap) return;
        folderMap.set('name', name);
    }, null);
}

/**
 * Move a folder to a new parent, updating its position.
 * Throws if the target parent is a descendant of the folder being moved.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             folderId
 * @param {string|null}        newParentFolderId
 * @param {number}             [position]  If omitted, the folder is appended at the end of the new parent's children.
 */
export function moveFolder(ydoc, ySourcePackage, folderId, newParentFolderId, position) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!folderId) throw new Error('[sourcePackageYMap] folderId is required');

    // Validate: cannot move into own descendant
    const descendants = getDescendantFolderIds(ySourcePackage, folderId);
    if (newParentFolderId && (newParentFolderId === folderId || descendants.has(newParentFolderId))) {
        throw new Error('[sourcePackageYMap] Cannot move a folder into its own descendant');
    }

    // Default position: append at end of new parent's sibling group
    const resolvedPosition = position != null
        ? position
        : computeAppendPosition(getFoldersAtLevel(ySourcePackage, newParentFolderId ?? null));

    ydoc.transact(() => {
        const foldersMap = getFoldersMap(ySourcePackage);
        if (!foldersMap) return;
        const folderMap = foldersMap.get(folderId);
        if (!folderMap) return;
        folderMap.set('parentFolderId', newParentFolderId ?? null);
        folderMap.set('position', resolvedPosition);
    }, null);
}

/**
 * Remove a folder and cascade-delete all descendant folders and their items.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             folderId
 */
export function removeFolder(ydoc, ySourcePackage, folderId) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!folderId) throw new Error('[sourcePackageYMap] folderId is required');

    // Collect all folder IDs to delete (the folder itself + all descendants)
    const descendantIds = getDescendantFolderIds(ySourcePackage, folderId);
    const folderIdsToDelete = new Set([folderId, ...descendantIds]);

    ydoc.transact(() => {
        const foldersMap = getFoldersMap(ySourcePackage);
        const itemsMap = getItemsMap(ySourcePackage);

        if (foldersMap) {
            folderIdsToDelete.forEach((id) => {
                foldersMap.delete(id);
            });
        }

        if (itemsMap) {
            // Remove all items whose parentFolderId is in the deleted set
            const itemsToDelete = [];
            itemsMap.forEach((itemMap, itemId) => {
                const parentId = itemMap.get('parentFolderId');
                if (folderIdsToDelete.has(parentId)) {
                    itemsToDelete.push(itemId);
                }
            });
            itemsToDelete.forEach((id) => itemsMap.delete(id));
        }
    }, null);
}

/**
 * Update the position of a folder for ordering among siblings.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             folderId
 * @param {number}             newPosition
 */
export function reorderFolder(ydoc, ySourcePackage, folderId, newPosition) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!folderId) throw new Error('[sourcePackageYMap] folderId is required');

    ydoc.transact(() => {
        const foldersMap = getFoldersMap(ySourcePackage);
        if (!foldersMap) return;
        const folderMap = foldersMap.get(folderId);
        if (!folderMap) return;
        folderMap.set('position', newPosition);
    }, null);
}

// ---------------------------------------------------------------------------
// Public write API — Items
// ---------------------------------------------------------------------------

/**
 * Create a new item in the source package.
 *
 * Duplicate detection: if an item with the same `localId` already exists in
 * the same `parentFolderId`, the existing item's ID is returned (no-op).
 *
 * Known fields stored explicitly: type, parentFolderId, localId, position, userId,
 * docId, docTitle, docUrl, docSource, docSourceType, classificationLevel,
 * content, originalContent, segmentId, code, start (JSON-stringified), end (JSON-stringified).
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {Object} opts
 * @param {string}      opts.type
 * @param {string|null} [opts.parentFolderId]
 * @param {string}      [opts.localId]
 * @param {number}      [opts.position]
 * @param {string}      [opts.userId]
 * @returns {string}  The item ID (new or existing duplicate)
 */
export function addItem(ydoc, ySourcePackage, {type, parentFolderId = null, localId, position, userId, ...data}) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!type) throw new Error('[sourcePackageYMap] type is required');

    // Derive localId if not provided
    const resolvedLocalId = localId ?? generateLocalId({...data, parentFolderId});

    let itemId = generateUUID();

    ydoc.transact(() => {
        const itemsMap = getOrCreateSubMap(ySourcePackage, ITEMS_KEY);

        // Duplicate detection inside transaction: read-your-own-writes semantics
        // within a single peer. Two concurrent peers can still both pass this check;
        // that is an accepted limitation of Yjs CRDT — eventual consistency will merge
        // both inserts and the UI deduplication layer handles display.
        if (resolvedLocalId) {
            let existingId = null;
            itemsMap.forEach((itemMap, id) => {
                if (existingId) return;
                if (
                    itemMap.get('localId') === resolvedLocalId &&
                    itemMap.get('parentFolderId') === (parentFolderId ?? null)
                ) {
                    existingId = id;
                }
            });
            if (existingId) {
                itemId = existingId; // signal to caller — no new map entry created
                return;
            }
        }

        const itemMap = new Y.Map();
        itemMap.set('type', type);
        itemMap.set('parentFolderId', parentFolderId ?? null);
        itemMap.set('localId', resolvedLocalId ?? null);
        itemMap.set('position', position ?? STEP);
        itemMap.set('createdAt', Date.now());
        itemMap.set('createdBy', userId ?? null);

        // Store known data fields
        if (data.docId !== undefined) itemMap.set('docId', data.docId ?? null);
        if (data.docTitle !== undefined) itemMap.set('docTitle', data.docTitle ?? null);
        if (data.docUrl !== undefined) itemMap.set('docUrl', data.docUrl ?? null);
        if (data.docSource !== undefined) itemMap.set('docSource', data.docSource ?? null);
        if (data.docSourceType !== undefined) itemMap.set('docSourceType', data.docSourceType ?? null);
        if (data.classificationLevel !== undefined) itemMap.set('classificationLevel', data.classificationLevel ?? null);
        if (data.content !== undefined) itemMap.set('content', data.content ?? null);
        if (data.originalContent !== undefined) itemMap.set('originalContent', data.originalContent ?? null);
        if (data.segmentId !== undefined) itemMap.set('segmentId', data.segmentId ?? null);
        if (data.code !== undefined) itemMap.set('code', data.code ?? null);

        // Store start/end as JSON strings
        if (data.start !== undefined) {
            itemMap.set('start', data.start != null ? JSON.stringify(data.start) : null);
        }
        if (data.end !== undefined) {
            itemMap.set('end', data.end != null ? JSON.stringify(data.end) : null);
        }

        itemsMap.set(itemId, itemMap);
    }, null);

    return itemId;
}

/**
 * Remove an item from the source package.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             itemId
 */
export function removeItem(ydoc, ySourcePackage, itemId) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!itemId) throw new Error('[sourcePackageYMap] itemId is required');

    ydoc.transact(() => {
        const itemsMap = getItemsMap(ySourcePackage);
        if (!itemsMap) return;
        itemsMap.delete(itemId);
    }, null);
}

/**
 * Move an item to a new parent folder, updating its position.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             itemId
 * @param {string|null}        newParentFolderId
 * @param {number}             position
 */
export function moveItem(ydoc, ySourcePackage, itemId, newParentFolderId, position) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!itemId) throw new Error('[sourcePackageYMap] itemId is required');

    ydoc.transact(() => {
        const itemsMap = getItemsMap(ySourcePackage);
        if (!itemsMap) return;
        const itemMap = itemsMap.get(itemId);
        if (!itemMap) return;
        itemMap.set('parentFolderId', newParentFolderId ?? null);
        itemMap.set('position', position);
    }, null);
}

/**
 * Update the position of an item for ordering among siblings.
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             itemId
 * @param {number}             newPosition
 */
export function reorderItem(ydoc, ySourcePackage, itemId, newPosition) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!itemId) throw new Error('[sourcePackageYMap] itemId is required');

    ydoc.transact(() => {
        const itemsMap = getItemsMap(ySourcePackage);
        if (!itemsMap) return;
        const itemMap = itemsMap.get(itemId);
        if (!itemMap) return;
        itemMap.set('position', newPosition);
    }, null);
}

/**
 * Merge key-value pairs onto an existing item Y.Map.
 * For future extensibility (e.g., update content).
 *
 * @param {import('yjs').Doc}  ydoc
 * @param {import('yjs').Map}  ySourcePackage
 * @param {string}             itemId
 * @param {Object}             updates  Key-value pairs to merge
 */
export function updateItem(ydoc, ySourcePackage, itemId, updates) {
    if (!ydoc || !ySourcePackage) throw new Error('[sourcePackageYMap] ydoc and ySourcePackage are required');
    if (!itemId) throw new Error('[sourcePackageYMap] itemId is required');
    if (!updates || typeof updates !== 'object') throw new Error('[sourcePackageYMap] updates must be an object');

    ydoc.transact(() => {
        const itemsMap = getItemsMap(ySourcePackage);
        if (!itemsMap) return;
        const itemMap = itemsMap.get(itemId);
        if (!itemMap) return;
        Object.entries(updates).forEach(([key, value]) => {
            itemMap.set(key, value);
        });
    }, null);
}

// ---------------------------------------------------------------------------
// Public read API
// ---------------------------------------------------------------------------

/**
 * Return all folders as a plain JS array `[{ id, name, parentFolderId, position, ... }]`.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @returns {Object[]}
 */
export function getAllFolders(ySourcePackage) {
    const foldersMap = getFoldersMap(ySourcePackage);
    if (!foldersMap) return [];
    const result = [];
    foldersMap.forEach((folderMap, folderId) => {
        result.push(deserialise(folderId, folderMap));
    });
    return result;
}

/**
 * Return all items as a plain JS array.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @returns {Object[]}
 */
export function getAllItems(ySourcePackage) {
    const itemsMap = getItemsMap(ySourcePackage);
    if (!itemsMap) return [];
    const result = [];
    itemsMap.forEach((itemMap, itemId) => {
        result.push(deserialise(itemId, itemMap));
    });
    return result;
}

/**
 * Return a single folder as a plain JS object, or `null` if not found.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string}            folderId
 * @returns {Object|null}
 */
export function getFolderById(ySourcePackage, folderId) {
    const foldersMap = getFoldersMap(ySourcePackage);
    if (!foldersMap) return null;
    const folderMap = foldersMap.get(folderId);
    if (!folderMap) return null;
    return deserialise(folderId, folderMap);
}

/**
 * Return a single item as a plain JS object, or `null` if not found.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string}            itemId
 * @returns {Object|null}
 */
export function getItemById(ySourcePackage, itemId) {
    const itemsMap = getItemsMap(ySourcePackage);
    if (!itemsMap) return null;
    const itemMap = itemsMap.get(itemId);
    if (!itemMap) return null;
    return deserialise(itemId, itemMap);
}

/**
 * Return folders whose `parentFolderId` matches, sorted by `position` ascending.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string|null}       parentFolderId  Pass `null` for root-level folders
 * @returns {Object[]}
 */
export function getFoldersAtLevel(ySourcePackage, parentFolderId) {
    return getAllFolders(ySourcePackage)
        .filter((f) => (f.parentFolderId ?? null) === (parentFolderId ?? null))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/**
 * Return items whose `parentFolderId` matches, sorted by `position` ascending.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string|null}       parentFolderId
 * @returns {Object[]}
 */
export function getItemsAtLevel(ySourcePackage, parentFolderId) {
    return getAllItems(ySourcePackage)
        .filter((i) => (i.parentFolderId ?? null) === (parentFolderId ?? null))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/**
 * Return a `Set<string>` of all descendant folder IDs (recursive).
 * Used for cascade delete and move validation.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string}            folderId  The root folder whose descendants to find
 * @returns {Set<string>}
 */
export function getDescendantFolderIds(ySourcePackage, folderId) {
    const result = new Set();
    const foldersMap = getFoldersMap(ySourcePackage);
    if (!foldersMap) return result;

    // BFS to collect all descendants. Guard is `!result.has(id)` (not-visited),
    // not `id !== folderId`, so the invariant is "never re-enqueue a visited node".
    const queue = [folderId];
    while (queue.length > 0) {
        const currentId = queue.shift();
        foldersMap.forEach((folderMap, id) => {
            if (folderMap.get('parentFolderId') === currentId && !result.has(id)) {
                result.add(id);
                queue.push(id);
            }
        });
    }

    return result;
}

/**
 * Count items in a folder (non-recursive). Used for UI badge.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {string}            folderId
 * @returns {number}
 */
export function countItemsByFolder(ySourcePackage, folderId) {
    return getItemsAtLevel(ySourcePackage, folderId).length;
}

// ---------------------------------------------------------------------------
// Exported observer API
// ---------------------------------------------------------------------------

/**
 * Subscribe to any change inside Y.Map("sourcePackage").
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeSourcePackage(ySourcePackage, callback) {
    if (!ySourcePackage || typeof callback !== 'function') return () => {
    };
    ySourcePackage.observeDeep(callback);
    return () => ySourcePackage.unobserveDeep(callback);
}

/**
 * Subscribe to changes in the `folders` sub-map only.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeFolders(ySourcePackage, callback) {
    if (!ySourcePackage || typeof callback !== 'function') return () => {
    };

    const handler = (events) => {
        const relevant = events.some((event) =>
            // Root-level change: the 'folders' key was added/modified
            (event.path.length === 0 && event.keysChanged && event.keysChanged.has(FOLDERS_KEY))
            // Deeper change: path starts with 'folders'
            || (event.path.length > 0 && event.path[0] === FOLDERS_KEY)
        );
        if (relevant) callback(events);
    };

    ySourcePackage.observeDeep(handler);
    return () => ySourcePackage.unobserveDeep(handler);
}

/**
 * Subscribe to changes in the `items` sub-map only.
 *
 * @param {import('yjs').Map} ySourcePackage
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeItems(ySourcePackage, callback) {
    if (!ySourcePackage || typeof callback !== 'function') return () => {
    };

    const handler = (events) => {
        const relevant = events.some((event) =>
            // Root-level change: the 'items' key was added/modified
            (event.path.length === 0 && event.keysChanged && event.keysChanged.has(ITEMS_KEY))
            // Deeper change: path starts with 'items'
            || (event.path.length > 0 && event.path[0] === ITEMS_KEY)
        );
        if (relevant) callback(events);
    };

    ySourcePackage.observeDeep(handler);
    return () => ySourcePackage.unobserveDeep(handler);
}

